import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useSessionContext, useUser } from '@shopware/composables'
import { useLocalWishlist, useSyncWishlist } from '#imports'
import type { UseProductWishlistReturn as BaseUseProductWishlistReturn } from '@shopware/composables'
import { ApiClientError } from '@shopware/api-client'

export type UseProductWishlistReturn = Omit<BaseUseProductWishlistReturn, 'addToWishlist'> & {
    /**
     * Adds product to wishlist. `config` is the raw Excentos configurator payload (JSON
     * string) for a configured product; forwarded to both the sync and the local (guest)
     * add path.
     */
    addToWishlist(config?: string): Promise<void>
}

/**
 * Override of the upstream `useProductWishlist`, which captures `productId` by value and
 * therefore ignores later variant changes on the detail page. Accepting a reactive id and
 * reading it via `toValue()` keeps add/remove/isInWishlist in sync with the selected variant.
 */
export function useProductWishlist(
    productId: MaybeRefOrGetter<string>,
): UseProductWishlistReturn {
    const { isLoggedIn } = useUser()
    const { refreshSessionContext } = useSessionContext()
    const {
        addToWishlist: addItem,
        removeFromWishlist: removeItem,
        items,
    } = useLocalWishlist()

    const {
        addToWishlistSync: addItemSync,
        removeFromWishlistSync: removeItemSync,
        items: itemsSync,
        getWishlistProducts,
    } = useSyncWishlist()

    async function isSessionGone(e: unknown): Promise<boolean> {
        if (!(e instanceof ApiClientError && (e.status === 401 || e.status === 403))) {
            return false
        }
        await refreshSessionContext()
        return true
    }

    async function removeFromWishlist() {
        if (!isLoggedIn.value) {
            await removeItem(toValue(productId))
            return
        }

        try {
            await removeItemSync(toValue(productId))
            await getWishlistProducts()
        } catch (e: unknown) {
            if (!await isSessionGone(e)) {
                throw e
            }
            // The server-side entry stays behind and reappears on the next login -- without
            // a session there is no way to reach it.
            await removeItem(toValue(productId))
        }
    }

    async function addToWishlist(config?: string) {
        if (!isLoggedIn.value) {
            await addItem(toValue(productId), config)
            return
        }

        try {
            await addItemSync(toValue(productId), config)
            await getWishlistProducts()
        } catch (e: unknown) {
            // The wishlist ids are loaded asynchronously on app start, so a click before
            // they arrive sees an empty `itemsSync`, reads isInWishlist as false and takes
            // this branch for a product that is already on the list. The client cannot know
            // the state at click time -- the server can, and it says the goal is achieved.
            // Reload the ids so the button reflects it.
            if (e instanceof ApiClientError
                && e.details?.errors?.[0]?.code === 'CHECKOUT__DUPLICATE_WISHLIST_PRODUCT') {
                await getWishlistProducts()
                return
            }

            if (!await isSessionGone(e)) {
                throw e
            }
            await addItem(toValue(productId), config)
        }
    }

    const isInWishlist = computed(() =>
        isLoggedIn.value
            ? !!itemsSync.value?.includes(toValue(productId))
            : !!items.value?.includes(toValue(productId)),
    )

    return {
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
    }
}
